import ApiService from './api';
import { safeArray } from '../utils/money';

export interface TransactionReport {
  summary: {
    total_transactions: number;
    total_amount: number;
    successful_transactions: number;
    failed_transactions: number;
    success_rate: number;
  };
  transactions: any[];
  charts: any;
}

export interface SummaryReport {
  revenue: {
    total: number;
    currency: string;
    by_currency: any[];
  };
  transactions: {
    count: number;
    by_status: any[];
  };
  refunds: {
    count: number;
    amount: number;
  };
  disputes: {
    count: number;
    amount: number;
  };
}

export interface ExportRequest {
  type: 'transactions' | 'orders' | 'refunds' | 'settlements' | 'disputes';
  format: 'csv' | 'excel' | 'pdf';
  start_date: string;
  end_date: string;
  filters?: Record<string, any>;
}

export interface ExportJob {
  id: string;
  type: string;
  format: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  download_url?: string;
  created_at: string;
  completed_at?: string;
  file_size?: string;
}

export class ReportService {
  /**
   * 获取交易报表
   */
  static async getTransactionReport(
    startDate: string,
    endDate: string,
    filters?: any
  ): Promise<TransactionReport> {
    const params = {
      start_date: startDate,
      end_date: endDate,
      ...filters,
    };
    const response = await ApiService.get<any>(
      '/merchant/payments/reports/transactions',
      params
    );
    // 后端返回 { data: [{date,count,amount}], period }，无 summary 字段。
    // 在此统一归一化出 summary，避免页面访问 undefined.summary 崩溃。
    const raw = response.data || {};
    const rows: any[] = Array.isArray(raw.data) ? raw.data : [];
    const totalTransactions = rows.reduce((s, r) => s + (r.count || 0), 0);
    const totalAmount = rows.reduce((s, r) => s + (r.amount || 0), 0);
    return {
      summary: {
        total_transactions: totalTransactions,
        total_amount: totalAmount,
        successful_transactions: totalTransactions,
        failed_transactions: 0,
        success_rate: totalTransactions > 0 ? 100 : 0,
      },
      transactions: rows,
      charts: raw.charts || null,
    };
  }

  /**
   * 获取摘要报表
   */
  static async getSummaryReport(
    startDate: string,
    endDate: string
  ): Promise<SummaryReport> {
    const params = {
      start_date: startDate,
      end_date: endDate,
    };
    const response = await ApiService.get<any>(
      '/merchant/payments/reports/summary',
      params
    );
    // 后端返回 { totals:{payments,refunds,fees,net}, payment_methods:[], period }。
    // 归一化为页面期望结构，缺失字段兜底，避免崩溃。
    const raw = response.data || {};
    const totals = raw.totals || {};
    const byStatus = Array.isArray(raw.payment_methods)
      ? raw.payment_methods.map((m: any) => ({
          status: m.payment_method_type || '其他',
          count: m.count || 0,
        }))
      : [];
    const txCount = byStatus.reduce((s: number, m: any) => s + (m.count || 0), 0);
    return {
      revenue: {
        total: totals.payments || 0,
        currency: raw.period?.currency || 'USD',
        by_currency: [],
      },
      transactions: {
        count: txCount,
        by_status: byStatus,
      },
      refunds: {
        count: 0,
        amount: totals.refunds || 0,
      },
      disputes: {
        count: 0,
        amount: 0,
      },
    };
  }

  /**
   * 导出报表
   */
  static async exportReport(data: ExportRequest): Promise<ExportJob> {
    const response = await ApiService.post<ExportJob>(
      '/merchant/reports/export',
      data
    );
    return response.data;
  }

  /**
   * 获取导出任务列表
   */
  static async listExports(
    page: number = 1,
    limit: number = 20
  ): Promise<{
    data: ExportJob[];
    meta: any;
  }> {
    const params = { page, limit };
    const response = await ApiService.get('/merchant/reports/exports', params);
    return {
      data: safeArray<ExportJob>(response.data),
      meta: response.meta || { total: 0, per_page: 20, current_page: 1, last_page: 1 },
    };
  }

  /**
   * 获取导出任务详情
   */
  static async getExport(id: string): Promise<ExportJob> {
    const response = await ApiService.get<ExportJob>(`/merchant/reports/exports/${id}`);
    return response.data;
  }

  /**
   * 下载报表
   */
  static async downloadReport(id: string, filename: string): Promise<void> {
    const export_job = await this.getExport(id);
    if (export_job.download_url) {
      await ApiService.download(export_job.download_url, filename);
    }
  }

  /**
   * 获取结算报表
   */
  static async getSettlementReport(
    startDate: string,
    endDate: string
  ): Promise<any> {
    const params = {
      start_date: startDate,
      end_date: endDate,
    };
    const response = await ApiService.get('/merchant/reports/settlements', params);
    return response.data;
  }

  /**
   * 获取争议报表
   */
  static async getDisputeReport(
    startDate: string,
    endDate: string
  ): Promise<any> {
    const params = {
      start_date: startDate,
      end_date: endDate,
    };
    const response = await ApiService.get('/merchant/reports/disputes', params);
    return response.data;
  }

  /**
   * 获取费用报表
   */
  static async getFeeReport(
    startDate: string,
    endDate: string
  ): Promise<any> {
    const params = {
      start_date: startDate,
      end_date: endDate,
    };
    const response = await ApiService.get('/merchant/reports/fees', params);
    return response.data;
  }
}

export default ReportService;
